import { useEffect, useState } from "react"
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Table,
  TimePicker,
  message,
} from "antd"
import { PlusOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import apiClient from "../../../Utils/apiClient"

const COUNTRIES = [
  { value: "", label: "All countries" },
  { value: "EMEA", label: "Region: EMEA" },
  { value: "NLAM", label: "Region: NLAM (NL + Americas)" },
  { value: "APAC", label: "Region: APAC" },
  { value: "IN", label: "India" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "NL", label: "Netherlands" },
  { value: "GB", label: "United Kingdom" },
  { value: "IE", label: "Ireland" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "BE", label: "Belgium" },
  { value: "ES", label: "Spain" },
  { value: "PT", label: "Portugal" },
  { value: "IT", label: "Italy" },
  { value: "PL", label: "Poland" },
  { value: "SE", label: "Sweden" },
  { value: "ZA", label: "South Africa" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "BR", label: "Brazil" },
  { value: "AR", label: "Argentina" },
  { value: "CL", label: "Chile" },
  { value: "CO", label: "Colombia" },
  { value: "SG", label: "Singapore" },
]

const IN_STATES = [
  "Andhra Pradesh",
  "Delhi",
  "Gujarat",
  "Karnataka",
  "Kerala",
  "Maharashtra",
  "Tamil Nadu",
  "Telangana",
  "Uttar Pradesh",
  "West Bengal",
]

const AE_STATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"]

const NL_STATES = [
  "North Holland",
  "South Holland",
  "Utrecht",
  "North Brabant",
  "Gelderland",
  "Overijssel",
  "Limburg",
  "Flevoland",
  "Friesland",
  "Groningen",
  "Drenthe",
  "Zeeland",
]

const US_STATES = [
  "California",
  "Texas",
  "New York",
  "Florida",
  "Illinois",
  "Washington",
  "Massachusetts",
  "Georgia",
  "North Carolina",
  "Virginia",
]

const toHm = (value, fallback = "18:00") => {
  if (!value) return fallback
  if (typeof value === "string") return value.slice(0, 5)
  if (typeof value.format === "function") return value.format("HH:mm")
  return fallback
}

const ShutdownReminderSettings = () => {
  const [form] = Form.useForm()
  const [holidayForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState(null)
  const holidayCountry = Form.useWatch("country", holidayForm)

  const applySettings = (result) => {
    setSettings(result)
    form.setFieldsValue({
      enabled: result.enabled !== false,
      workStart: dayjs(result.workStart || "09:00", "HH:mm"),
      workEnd: dayjs(result.workEnd || result.sendTime || "18:00", "HH:mm"),
      windowMinutes: result.windowMinutes || 120,
      overnightEnabled: result.overnightEnabled !== false,
      overnightWindowMinutes: result.overnightWindowMinutes || 120,
      noRebootDays: result.noRebootDays || 3,
      skipWeekends: result.skipWeekends !== false,
      skipHolidays: result.skipHolidays !== false,
      ccAdmins: result.ccAdmins !== false,
      adminEmails: (result.adminEmails || []).join(", "),
    })
  }

  const payloadFromForm = (values, holidays) => ({
    enabled: Boolean(values.enabled),
    workStart: toHm(values.workStart, "09:00"),
    workEnd: toHm(values.workEnd, "18:00"),
    sendTime: toHm(values.workEnd, "18:00"),
    windowMinutes: Number(values.windowMinutes) || 120,
    overnightEnabled: Boolean(values.overnightEnabled),
    overnightWindowMinutes: Number(values.overnightWindowMinutes) || 120,
    noRebootDays: Number(values.noRebootDays) || 3,
    skipWeekends: Boolean(values.skipWeekends),
    skipHolidays: Boolean(values.skipHolidays),
    ccAdmins: Boolean(values.ccAdmins),
    adminEmails: String(values.adminEmails || "")
      .split(/[,;\s]+/)
      .filter(Boolean),
    holidays,
  })

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiClient("settings/shutdown-reminder", "GET")
      if (res?.success) applySettings(res.result)
    } catch (err) {
      message.error("Could not load shutdown reminder settings")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const save = async () => {
    const values = await form.validateFields()
    setLoading(true)
    try {
      const res = await apiClient(
        "settings/shutdown-reminder",
        "PUT",
        payloadFromForm(values, settings?.holidays || [])
      )
      if (res?.success) {
        message.success(res.message)
        applySettings(res.result)
      } else {
        message.error(res?.message || "Could not save")
      }
    } catch (err) {
      message.error(err.message || "Could not save shutdown reminder")
    } finally {
      setLoading(false)
    }
  }

  const saveHolidays = async (holidays) => {
    setLoading(true)
    try {
      const values = form.getFieldsValue()
      const res = await apiClient(
        "settings/shutdown-reminder",
        "PUT",
        payloadFromForm(values, holidays)
      )
      if (res?.success) {
        applySettings(res.result)
        return true
      }
      message.error(res?.message || "Could not update holidays")
      return false
    } catch (err) {
      message.error(err.message || "Could not update holidays")
      return false
    } finally {
      setLoading(false)
    }
  }

  const addHoliday = async () => {
    const values = await holidayForm.validateFields()
    const date = values.date ? values.date.format("YYYY-MM-DD") : ""
    if (!date) {
      message.warning("Pick a holiday date")
      return
    }
    const next = [
      ...(settings?.holidays || []),
      {
        date,
        name: values.name || "",
        country: values.country || "",
        state: values.state || "",
        recurring: Boolean(values.recurring),
      },
    ]
    const ok = await saveHolidays(next)
    if (ok) {
      holidayForm.resetFields()
      message.success("Holiday added")
    }
  }

  const removeHoliday = async (index) => {
    const next = (settings?.holidays || []).filter((_, i) => i !== index)
    const ok = await saveHolidays(next)
    if (ok) message.success("Holiday removed")
  }

  const stateOptions =
    holidayCountry === "IN"
      ? IN_STATES
      : holidayCountry === "AE"
      ? AE_STATES
      : holidayCountry === "NL"
      ? NL_STATES
      : holidayCountry === "US"
      ? US_STATES
      : []

  return (
    <Card
      className="ops-card mb-3"
      loading={loading && !settings}
      title="End-of-day shutdown reminder"
      extra={
        <Button type="primary" onClick={save} loading={loading}>
          Save schedule
        </Button>
      }
    >
      <p className="user-activity__muted" style={{ marginTop: 0 }}>
        End-of-day: one reminder after local work end if the PC is still on.
        Overnight: morning email if left on, put to sleep instead of shut down,
        or not restarted for several days. Times use each laptop&apos;s clock
        (EMEA / NLAM / India / UAE / APAC).
      </p>
      <Form form={form} layout="vertical">
        <Form.Item name="enabled" label="End-of-day still-on reminder" valuePropName="checked">
          <Switch checkedChildren="On" unCheckedChildren="Off" />
        </Form.Item>
        <div className="form-inline-fields form-inline-fields--3">
          <Form.Item
            name="workStart"
            label="Work start"
            extra="Device local time · default 09:00"
          >
            <TimePicker format="HH:mm" className="w-100" />
          </Form.Item>
          <Form.Item
            name="workEnd"
            label="Work end / remind after"
            extra="Device local · one reminder per day"
          >
            <TimePicker format="HH:mm" className="w-100" />
          </Form.Item>
          <Form.Item
            name="windowMinutes"
            label="Catch window (minutes)"
            extra="How long after work end to send"
          >
            <InputNumber min={15} max={360} step={15} className="w-100" />
          </Form.Item>
        </div>
        <Form.Item
          name="overnightEnabled"
          label="Overnight / sleep / no-restart reminder"
          extra="Morning email (after local work start) if left on overnight, slept instead of shut down, or uptime exceeds the days below."
          valuePropName="checked"
        >
          <Switch checkedChildren="On" unCheckedChildren="Off" />
        </Form.Item>
        <div className="form-inline-fields form-inline-fields--2">
          <Form.Item
            name="overnightWindowMinutes"
            label="Morning catch window"
            extra="Minutes after work start to check"
          >
            <InputNumber min={15} max={360} step={15} className="w-100" />
          </Form.Item>
          <Form.Item
            name="noRebootDays"
            label="No-restart threshold"
            extra="Days without reboot before flagging"
          >
            <InputNumber min={1} max={30} className="w-100" />
          </Form.Item>
        </div>
        <Form.Item name="skipWeekends" label="Skip Saturday and Sunday" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="skipHolidays" label="Skip listed holidays" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="ccAdmins"
          label="CC administrators"
          extra="Uses the Xpanse admin and report mailboxes, plus any extra addresses below."
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item name="adminEmails" label="Extra admin addresses">
          <Input placeholder="it@company.com, helpdesk@company.com" />
        </Form.Item>
      </Form>

      <h4>Holiday list</h4>
      <p className="user-activity__muted">
        Leave country empty to apply everywhere. Add a state to limit the
        holiday to that region (for example Karnataka or Dubai).
      </p>
      <Form form={holidayForm} layout="inline" className="mb-3" style={{ rowGap: 12 }}>
        <Form.Item name="date" rules={[{ required: true, message: "Date" }]}>
          <DatePicker />
        </Form.Item>
        <Form.Item name="name">
          <Input placeholder="Holiday name" />
        </Form.Item>
        <Form.Item name="country" initialValue="">
          <Select style={{ width: 200 }} options={COUNTRIES} />
        </Form.Item>
        <Form.Item name="state">
          {stateOptions.length ? (
            <Select
              allowClear
              showSearch
              style={{ width: 180 }}
              placeholder="State / emirate"
              options={stateOptions.map((item) => ({ value: item, label: item }))}
            />
          ) : (
            <Input placeholder="State (optional)" />
          )}
        </Form.Item>
        <Form.Item name="recurring" valuePropName="checked">
          <Switch checkedChildren="Every year" unCheckedChildren="This year" />
        </Form.Item>
        <Form.Item>
          <Button icon={<PlusOutlined />} onClick={addHoliday}>
            Add holiday
          </Button>
        </Form.Item>
      </Form>
      <Table
        size="small"
        rowKey={(row, index) => row._id || `${row.date}-${index}`}
        pagination={false}
        dataSource={settings?.holidays || []}
        locale={{ emptyText: "No holidays yet. Weekends can still be skipped above." }}
        columns={[
          { title: "Date", dataIndex: "date", width: 130 },
          { title: "Name", dataIndex: "name", render: (value) => value || "—" },
          {
            title: "Country",
            dataIndex: "country",
            width: 110,
            render: (value) => value || "All",
          },
          {
            title: "State",
            dataIndex: "state",
            render: (value) => value || "All",
          },
          {
            title: "Repeats",
            dataIndex: "recurring",
            width: 90,
            render: (value) => (value ? "Yearly" : "Once"),
          },
          {
            title: "",
            key: "remove",
            width: 90,
            render: (_, __, index) => (
              <Button type="link" danger onClick={() => removeHoliday(index)}>
                Remove
              </Button>
            ),
          },
        ]}
      />
    </Card>
  )
}

export default ShutdownReminderSettings
